import React from "react";
import { Card, Table } from "react-bootstrap";
import {
  COLUMN_TYPES,
  DISTRIBUTION_ENGINE_LABELS,
  SECTION_TYPES,
  type LayoutKpiMeta,
  type RenderedFieldListSection,
  type RenderedLayoutSection,
  type RenderedTableSection,
} from "@features/presentationLayout/types/presentationLayout";
import { formatLayoutValue, formulaHint } from "@features/presentationLayout/utils/layoutForm";
import { LAYOUT_PREVIEW_SECTION_TEST_ID } from "@features/presentationLayout/constants/presentationLayoutConstants";

const DEFAULT_TOTALS_LABEL = "TOTAL";
const DEFAULT_ROW_TOTAL_LABEL = "Total";

function FieldListSection({ section }: { section: RenderedFieldListSection }) {
  return (
    <Table responsive className="mb-0 align-middle" data-testid={`layout-field-list-${section.section_id}`}>
      {section.row_header_label ? (
        <thead>
          <tr>
            <th>{section.row_header_label}</th>
            <th className="text-end">Value</th>
          </tr>
        </thead>
      ) : null}
      <tbody>
        {section.rows.map((row) => (
          <tr key={row.row_id}>
            <th scope="row" className="fw-medium">{row.label || row.kpi?.kpi_name}</th>
            <td className="text-end">{formatLayoutValue(row.value, row.kpi)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function TableSection({ section }: { section: RenderedTableSection }) {
  const isDistribution = section.section_type === SECTION_TYPES.DISTRIBUTION_TABLE;
  const isBinding = section.section_type === SECTION_TYPES.BINDING_TABLE;
  const hasRowTotals = section.rows.some((row) => row.row_total !== undefined);
  const firstKpiColumn = section.columns.find((column) => column.column_type === COLUMN_TYPES.KPI_BINDING)?.kpi ?? null;
  const distributionKpi = isDistribution ? section.rows[0]?.kpi ?? null : null;

  /**
   * Distribution amounts share the row KPI's unit; matrix KPI columns use their own KPI;
   * Binding table Value cells use per-cell KPI meta when present.
   */
  const cellKpi = (
    columnIndex: number,
    rowKpi?: LayoutKpiMeta | null,
    cellKpis?: Array<LayoutKpiMeta | null>
  ): LayoutKpiMeta | null => {
    const column = section.columns[columnIndex];
    if (column?.column_type === COLUMN_TYPES.KPI_BINDING) return column.kpi;
    if (isBinding && column?.column_type === COLUMN_TYPES.VALUE) {
      return cellKpis?.[columnIndex] ?? null;
    }
    if (isDistribution && column?.column_type !== COLUMN_TYPES.CALCULATED) return rowKpi ?? null;
    return null;
  };

  return (
    <Table responsive bordered className="mb-0 align-middle" data-testid={`layout-table-${section.section_id}`}>
      <thead>
        <tr>
          <th>{section.row_header_label ?? ""}</th>
          {section.columns.map((column) => (
            <th
              key={column.column_id}
              className="text-end"
              title={column.column_type === COLUMN_TYPES.CALCULATED ? formulaHint(column, section.columns) : undefined}
            >
              {column.label}
            </th>
          ))}
          {hasRowTotals ? <th className="text-end">{DEFAULT_ROW_TOTAL_LABEL}</th> : null}
        </tr>
      </thead>
      <tbody>
        {section.rows.map((row) => (
          <tr key={row.row_id}>
            <th scope="row" className="fw-medium">
              {row.label || row.kpi?.kpi_name}
              {/* {row.distribution_engine ? (
                <div className="text-muted small fw-normal">{DISTRIBUTION_ENGINE_LABELS[row.distribution_engine]}</div>
              ) : null} */}
            </th>
            {row.cells.map((value, index) => (
              <td key={section.columns[index]?.column_id ?? index} className="text-end">
                {formatLayoutValue(value, cellKpi(index, row.kpi, row.cell_kpis))}
              </td>
            ))}
            {hasRowTotals ? <td className="text-end fw-semibold">{formatLayoutValue(row.row_total, firstKpiColumn)}</td> : null}
          </tr>
        ))}
      </tbody>
      {section.column_totals ? (
        <tfoot>
          <tr className="fw-semibold">
            <th scope="row">{section.totals_label || DEFAULT_TOTALS_LABEL}</th>
            {section.column_totals.map((value, index) => (
              <td key={section.columns[index]?.column_id ?? index} className="text-end">
                {formatLayoutValue(value, cellKpi(index, distributionKpi))}
              </td>
            ))}
            {hasRowTotals ? <td className="text-end">{formatLayoutValue(section.grand_total, firstKpiColumn)}</td> : null}
          </tr>
        </tfoot>
      ) : null}
    </Table>
  );
}

export const LayoutSection: React.FC<{ section: RenderedLayoutSection }> = ({ section }) => {
  return (
    <Card className="mb-3" data-testid={`${LAYOUT_PREVIEW_SECTION_TEST_ID}-${section.section_id}`}>
      <Card.Header className="fw-semibold">{section.title}</Card.Header>
      <Card.Body className="p-0">
        {section.section_type === SECTION_TYPES.FIELD_LIST
          ? <FieldListSection section={section} />
          : <TableSection section={section} />}
      </Card.Body>
    </Card>
  );
};

type Props = {
  sections: RenderedLayoutSection[];
  emptyMessage?: string;
};

/** Renders a named layout's sections: the report page passes live values, the admin preview passes empty ones. */
export default function LayoutSections({ sections, emptyMessage = "This layout has no sections yet." }: Props) {
  if (sections.length === 0) {
    return <p className="text-muted mb-0" data-testid="layout-sections-empty">{emptyMessage}</p>;
  }

  return (
    <div data-testid="layout-sections">
      {sections.map((section) => (
        <div key={section.section_id}>
          <LayoutSection section={section} />
        </div>
      ))}
    </div>
  );
}
